import React, { useState, useEffect, ReactElement } from "react";
import { BannerType, useBanner } from "@/libs/hooks/useBanner";
import { BlocksRenderer } from "@strapi/blocks-react-renderer";
import { IconAlertTriangle, IconInfoCircle, IconX } from "@tabler/icons-react";
import { Banner } from "flowbite-react";
import Link from "next/link";
import { cloneDeep } from "lodash";
import useGAnalytics, {
  EventCategory,
} from "@/libs/hooks/tracking/useGAnalytics";

type IconNames = "info-circle" | "alert-triangle";

export const BannerComponent: React.FC = () => {
  const { banners, dismiss } = useBanner();
  const [isBannerVisible, setIsBannerVisible] = useState(false);
  const [onTheWayOut, setOnTheWayOut] = useState(false);

  const [currentBanner, setCurrentBanner] = useState<BannerType>();
  const [nextBanner, setNextBanner] = useState<BannerType>();

  const { trackEvent } = useGAnalytics();

  const setBanners = () => {
    setCurrentBanner(cloneDeep(banners[0]));
    setNextBanner(cloneDeep(banners[1]));

    setTimeout(() => {
      setOnTheWayOut(false);
    }, 500);
  };

  useEffect(() => {
    if (currentBanner && nextBanner && onTheWayOut) {
      setTimeout(() => {
        setBanners();
      }, nextBanner.attributes.timeoutMs || 2000);
    } else if (!currentBanner) {
      setBanners();
    } else if (banners.length === 0) {
      setBanners();
    }
  }, [banners]);

  useEffect(() => {
    if (currentBanner) {
      const timer = setTimeout(
        () => setIsBannerVisible(true),
        currentBanner?.attributes?.timeoutMs || 2500
      );
      return () => clearTimeout(timer);
    }
  }, [currentBanner]);

  const handleDismiss = (bannerId: number) => {
    setOnTheWayOut(true);
    trackEvent(
      "banner-dismissed-clicked",
      EventCategory.USER_INTERACTION,
      "banner-dismissed",
      bannerId.toString()
    );
    setTimeout(() => {
      dismiss(bannerId);
    }, 500);
  };

  const getBackgroundStyle = (bgColor: string) => {
    // Map old pizza colors to vintage equivalents
    const colorMap: { [key: string]: string } = {
      "pizza-yellow": "bg-vintage-ochre",
      "pizza-red": "bg-vintage-terracotta",
      "pizza-blue": "bg-vintage-navy",
      "pizza-gray": "bg-vintage-walnut",
    };
    return colorMap[bgColor] || "bg-vintage-burgundy";
  };

  const textColorClassName = (bgColor: string) => {
    switch (bgColor) {
      case "pizza-yellow":
      case "vintage-ochre":
        return "text-vintage-espresso";
      default:
        return "text-vintage-cream";
    }
  };

  const renderIcon = (iconName: IconNames): ReactElement | null => {
    const icons: { [key in IconNames]: ReactElement } = {
      "info-circle": <IconInfoCircle className="mr-3 min-w-6" size={24} />,
      "alert-triangle": <IconAlertTriangle className="mr-3 min-w-6" size={24} />,
    };
    return icons[iconName] || null;
  };

  return (
    <>
      {isBannerVisible && currentBanner && (
        <Banner
          key={currentBanner.id}
          className={`fixed top-0 w-full z-[300] font-serif
          ${onTheWayOut ? "banner-slide-up" : "banner-slide-down"}`}
        >
          <div
            className={`flex w-full justify-between shadow-vintage ${getBackgroundStyle(
              currentBanner.attributes.bgColor
            )} p-4 border-b border-vintage-ochre/30`}
          >
            {/* Decorative elements */}
            <div className="absolute left-4 top-1/2 -translate-y-1/2 w-2 h-2 bg-vintage-ochre/40 rotate-45 hidden md:block" />
            <div className="absolute right-16 top-1/2 -translate-y-1/2 w-2 h-2 bg-vintage-ochre/40 rotate-45 hidden md:block" />

            <div className="mx-auto flex items-center">
              <p
                className={`flex items-center font-normal ${textColorClassName(
                  currentBanner.attributes.bgColor
                )}`}
              >
                {renderIcon(currentBanner.attributes.tablerIcon as IconNames)}
                <span className="[&_p]:inline text-base md:text-lg tracking-wide">
                  <BlocksRenderer
                    blocks={{
                      link: ({ url, children }) => (
                        <Link
                          href={url}
                          onClick={() =>
                            currentBanner.attributes.dismissOnLinkClick &&
                            handleDismiss(currentBanner.id)
                          }
                          target={currentBanner.attributes.linksTarget}
                          className="text-vintage-ochre hover:text-vintage-gold underline underline-offset-2 decoration-vintage-ochre/50 hover:decoration-vintage-gold transition-colors font-medium"
                        >
                          {children}
                        </Link>
                      ),
                    }}
                    content={currentBanner.attributes.text}
                  />
                </span>
              </p>
            </div>
            <Banner.CollapseButton
              color="transparent"
              className={`border-0 bg-transparent hover:bg-vintage-cream/10 rounded-full p-2 transition-colors ${textColorClassName(
                currentBanner.attributes.bgColor
              )}`}
              onClick={() => handleDismiss(currentBanner.id)}
            >
              <IconX size={20} />
            </Banner.CollapseButton>
          </div>
        </Banner>
      )}
    </>
  );
};
